/**
 * Dashboard do CONECTA PKU (HNS-1594).
 *
 * Entra na Home pelo motor de dashboards do programa: a chave
 * `conecta_pku_dashboard` em `configuracoes.graficos_dashboard` traz este bloco.
 *
 * Le o endpoint /dashboards/conecta_pku/, que agrega a mesma base do Relatorio
 * Semanal PTC, e responde a pergunta que a operacao faz toda semana: quantos
 * pacientes entraram, onde a jornada trava, quantos testes deram positivo e quanto
 * a logistica custou.
 *
 * E um bloco unico (nao um por grafico) porque todos os recortes vem da mesma
 * consulta - registrar sete chaves faria sete requisicoes identicas.
 */
import React, { Component } from "react";
import { Card, Row, Col, Spinner, Alert, Form, Button, Badge, Table } from "react-bootstrap";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { FaFilter, FaEraser } from "react-icons/fa";
import api from "../../../services/api";
import { getCurrentUser, tiposAcesso } from "../../../common/utils";

//Quem acompanha o programa: operacao, gestao interna, a industria e o admin.
//Prestador/laboratorio/psicologa nao entram - veem apenas os seus atendimentos.
const podeVerDashboard = () => {
    const usuario = getCurrentUser();
    if (usuario['is_superuser']) return true;

    const tipo = usuario['tipo_usuario'];
    if (tipo === '' || tipo === 'operador') return true;   // admin e navegadora/operadora

    if (tipo === 'gestor') {
        const acessos = tiposAcesso() || {};
        const tipoGestor = acessos.gestor?.tipo_gestor;
        return tipoGestor === 'interno' || tipoGestor === 'industria';
    }

    return false;
};

const COR_PRINCIPAL = '#00ADD8';
const COR_APOIO = '#333159';
const COR_ALERTA = '#a41c12';
const CINZA = '#6b7a8d';

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const formataMes = (chave) => {
    if (!chave || !/^\d{4}-\d{2}$/.test(chave)) return chave || '-';
    const [ano, mes] = chave.split('-');
    return `${MESES_CURTOS[Number(mes) - 1]}/${ano.slice(2)}`;
};

const formataReais = (valor) => {
    const numero = Number(valor || 0);
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formataDias = (valor) => (valor === null || valor === undefined ? '-' : `${valor} d`);

class ConectaPkuDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dados: null,
            carregando: true,
            erro: '',
            data_de: '',
            data_ate: '',
        };
    }

    componentDidMount() {
        this.carregar();
    }

    getProgramaId = () => {
        const usuario = getCurrentUser();
        return this.props.programaId || usuario['ambiente_programa_id'] || null;
    };

    carregar = async () => {
        const programa_id = this.getProgramaId();

        if (!programa_id) {
            this.setState({ carregando: false, erro: 'Selecione um programa para ver os indicadores.' });
            return;
        }

        this.setState({ carregando: true, erro: '' });

        try {
            const params = { programa_id };
            if (this.state.data_de) params.data_de = this.state.data_de;
            if (this.state.data_ate) params.data_ate = this.state.data_ate;

            const { data } = await api.get('/dashboards/conecta_pku/', { params });
            this.setState({ dados: data, carregando: false });
        } catch (e) {
            console.error('Erro ao carregar o dashboard do CONECTA PKU:', e);
            this.setState({
                carregando: false,
                erro: e?.response?.data?.error || 'Não foi possível carregar os indicadores.',
            });
        }
    };

    limpar = () => this.setState({ data_de: '', data_ate: '' }, this.carregar);

    renderIndicador = (titulo, valor, complemento, cor = COR_APOIO) => (
        <Col xs={6} md={4} xl={2} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                    <div style={{ fontSize: '11px', color: CINZA, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                        {titulo}
                    </div>
                    {/* valor longo (ex.: custo em reais) reduz a fonte para nao quebrar em duas linhas */}
                    <div style={{
                        fontSize: String(valor).length > 9 ? '19px' : '26px',
                        fontWeight: 700, color: cor, lineHeight: 1.25, whiteSpace: 'nowrap'
                    }}>{valor}</div>
                    {complemento && <div style={{ fontSize: '11px', color: CINZA }}>{complemento}</div>}
                </Card.Body>
            </Card>
        </Col>
    );

    renderCard = (titulo, subtitulo, conteudo, altura = 320, largura = 6) => (
        <Col lg={largura} className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pb-0">
                    <div className="fw-bold">{titulo}</div>
                    {subtitulo && <small className="text-muted">{subtitulo}</small>}
                </Card.Header>
                <Card.Body style={{ height: `${altura}px` }}>{conteudo}</Card.Body>
            </Card>
        </Col>
    );

    semDados = (mensagem = 'Sem registros no período') => (
        <div className="d-flex align-items-center justify-content-center h-100">
            <span className="text-muted" style={{ fontSize: '13px' }}>{mensagem}</span>
        </div>
    );

    render() {
        const { dados, carregando, erro, data_de, data_ate } = this.state;

        if (!podeVerDashboard()) return null;
        if (carregando) return <div className="text-center py-5"><Spinner animation="border" /></div>;
        if (erro) return <Alert variant="warning" className="m-4">{erro}</Alert>;

        const resumo = dados?.resumo || {};
        const funil = dados?.funil_status || [];
        const tempos = (dados?.tempos_jornada || []).filter(t => t.dias_medio !== null);
        // operacao pediu (HNS-1594 bug 26) ver aumento/reducao no lugar do positivo/negativo.
        // Preferimos `resposta_variacao` (agregacao pelo campo slug resultado-variacao) e caimos
        // no `resposta_teste` antigo se o backend ainda nao tiver publicado a nova chave.
        const respostaFonte = (dados?.resposta_variacao?.length ? dados.resposta_variacao : dados?.resposta_teste) || [];
        const resposta = respostaFonte.filter(r => r.value > 0);
        const logistica = dados?.logistica || [];
        const centros = dados?.centros || [];
        const evolucao = dados?.evolucao_mensal || [];

        //o trecho mais lento da jornada e o que a operacao precisa atacar primeiro
        const gargalo = tempos.length
            ? tempos.reduce((pior, atual) => (atual.dias_medio > pior.dias_medio ? atual : pior))
            : null;

        return (
            <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold m-0">CONECTA PKU — jornada do Teste de Responsividade</h5>
                        <small className="text-muted">Indicadores consolidados do programa</small>
                    </div>
                </div>

                {/* filtros */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-3">
                        <Row className="align-items-end g-2">
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Data de</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_de}
                                    onChange={(e) => this.setState({ data_de: e.target.value })}
                                />
                            </Col>
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Data até</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_ate}
                                    onChange={(e) => this.setState({ data_ate: e.target.value })}
                                />
                            </Col>
                            <Col sm={12} lg={6} className="d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={this.carregar}>
                                    <FaFilter className="me-1" /> Filtrar
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={this.limpar}>
                                    <FaEraser className="me-1" /> Limpar
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* indicadores */}
                <Row>
                    {this.renderIndicador('Pacientes', resumo.pacientes ?? 0, 'no período')}
                    {this.renderIndicador('Testes concluídos', resumo.testes_concluidos ?? 0,
                        `${resumo.pacientes ? Math.round((resumo.testes_concluidos / resumo.pacientes) * 100) : 0}% da base`)}
                    {this.renderIndicador('Encaminhados para tratamento', resumo.positivos ?? 0, 'com prescrição registrada', '#1e7e34')}
                    {this.renderIndicador('Taxa de encaminhamento',
                        resumo.taxa_positivo === null || resumo.taxa_positivo === undefined ? '-' : `${resumo.taxa_positivo}%`,
                        'sobre os testes com resultado', '#1e7e34')}
                    {this.renderIndicador('Duração média', formataDias(resumo.duracao_media_dias), 'início → fim do teste')}
                    {this.renderIndicador('Custo logística', formataReais(resumo.custo_logistica), 'IN + OUT no período', COR_PRINCIPAL)}
                </Row>

                {gargalo && (
                    <Alert variant="light" className="border shadow-sm py-2 mb-4" style={{ fontSize: '13px' }}>
                        <strong>Maior espera da jornada:</strong> {gargalo.etapa} — média de {gargalo.dias_medio} dias
                        {gargalo.dias_maximo !== null && <> (pior caso: {gargalo.dias_maximo} dias)</>}
                        <Badge bg="light" text="dark" className="ms-2 fw-normal">{gargalo.amostra} pacientes</Badge>
                    </Alert>
                )}

                <Row>
                    {/* jornada: onde trava */}
                    {this.renderCard(
                        'Tempo médio por trecho da jornada',
                        'Dias entre um marco e o seguinte — quanto maior, mais tempo o paciente espera',
                        tempos.length ? (
                            <ResponsiveBar
                                data={[...tempos].reverse().map(t => ({ etapa: t.etapa, dias: t.dias_medio }))}
                                keys={['dias']}
                                indexBy="etapa"
                                layout="horizontal"
                                margin={{ top: 10, right: 30, bottom: 40, left: 210 }}
                                padding={0.35}
                                colors={[COR_PRINCIPAL]}
                                labelTextColor="#fff"
                                axisBottom={{ tickSize: 0, tickPadding: 8, legend: 'dias', legendPosition: 'middle', legendOffset: 32 }}
                                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                enableGridX
                                enableGridY={false}
                                tooltip={({ data }) => (
                                    <div style={{ background: '#fff', padding: '6px 10px', border: '1px solid #dfe5ee', fontSize: '12px' }}>
                                        <strong>{data.etapa}</strong><br />média de {data.dias} dias
                                    </div>
                                )}
                            />
                        ) : this.semDados('Sem datas suficientes para calcular os prazos'),
                        340, 7
                    )}

                    {/* resposta ao teste */}
                    {this.renderCard(
                        'Resposta ao Teste de Responsividade',
                        'Distribuição dos resultados liberados',
                        resposta.length ? (
                            <ResponsivePie
                                data={resposta}
                                margin={{ top: 16, right: 16, bottom: 56, left: 16 }}
                                innerRadius={0.6}
                                padAngle={0.8}
                                cornerRadius={3}
                                colors={{ datum: 'data.color' }}
                                enableArcLinkLabels={false}
                                arcLabelsTextColor="#fff"
                                legends={[{
                                    anchor: 'bottom', direction: 'row', translateY: 44,
                                    itemWidth: 105, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados(),
                        340, 5
                    )}
                </Row>

                <Row>
                    {/* evolucao mensal */}
                    {this.renderCard(
                        'Entradas e conclusões por mês',
                        'Compara quantos pacientes entraram com quantos concluíram o teste',
                        evolucao.length ? (
                            <ResponsiveBar
                                data={evolucao.map(m => ({
                                    mes: formataMes(m.mes),
                                    Entradas: m.entradas,
                                    Concluídos: m.testes_concluidos,
                                }))}
                                keys={['Entradas', 'Concluídos']}
                                indexBy="mes"
                                groupMode="grouped"
                                margin={{ top: 10, right: 20, bottom: 60, left: 45 }}
                                padding={0.3}
                                colors={[COR_APOIO, COR_PRINCIPAL]}
                                labelSkipHeight={14}
                                labelTextColor="#fff"
                                axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: -35 }}
                                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                legends={[{
                                    dataFrom: 'keys', anchor: 'bottom', direction: 'row', translateY: 54,
                                    itemWidth: 92, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados(),
                        340, 7
                    )}

                    {/* funil por status */}
                    {this.renderCard(
                        'Pacientes por status do programa',
                        'Onde a base está parada hoje',
                        funil.length ? (
                            <ResponsiveBar
                                data={[...funil].reverse()}
                                keys={['value']}
                                indexBy="label"
                                layout="horizontal"
                                margin={{ top: 10, right: 24, bottom: 30, left: 130 }}
                                padding={0.35}
                                colors={[COR_APOIO]}
                                labelTextColor="#fff"
                                axisBottom={{ tickSize: 0, tickPadding: 8 }}
                                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                enableGridX
                                enableGridY={false}
                            />
                        ) : this.semDados(),
                        340, 5
                    )}
                </Row>

                <Row>
                    {/* centros */}
                    <Col lg={7} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pb-0">
                                <div className="fw-bold">Desempenho por centro executor</div>
                                <small className="text-muted">Volume, encaminhamento para tratamento e duração do teste em cada centro</small>
                            </Card.Header>
                            <Card.Body>
                                {centros.length ? (
                                    <Table hover responsive size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>Centro</th>
                                                <th className="text-center">Pacientes</th>
                                                <th className="text-center">Encaminhados</th>
                                                <th className="text-center">Taxa</th>
                                                <th className="text-center">Duração média</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {centros.map((c) => (
                                                <tr key={c.centro}>
                                                    <td>{c.centro}</td>
                                                    <td className="text-center">{c.pacientes}</td>
                                                    <td className="text-center">{c.positivos}</td>
                                                    <td className="text-center">
                                                        {c.taxa_positivo === null ? '-' : `${c.taxa_positivo}%`}
                                                    </td>
                                                    <td className="text-center">{formataDias(c.duracao_media_dias)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : this.semDados()}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* logistica */}
                    <Col lg={5} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pb-0">
                                <div className="fw-bold">Logística por operador</div>
                                <small className="text-muted">Custo IN/OUT e prazo médio de entrega</small>
                            </Card.Header>
                            <Card.Body style={{ overflowX: 'auto' }}>
                                {logistica.length ? (
                                    <Table hover responsive size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>Operador</th>
                                                <th className="text-center">Envios</th>
                                                <th className="text-end">Total</th>
                                                <th className="text-center" title="Prazo médio de entrega no paciente">IN</th>
                                                <th className="text-center" title="Prazo médio de devolução ao centro">OUT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logistica.map((l) => (
                                                <tr key={l.operador}>
                                                    <td>{l.operador}</td>
                                                    <td className="text-center">{l.envios}</td>
                                                    <td className="text-end">{formataReais(l.total)}</td>
                                                    <td className="text-center">{formataDias(l.dias_medio_in)}</td>
                                                    <td className="text-center">{formataDias(l.dias_medio_out)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : this.semDados()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default ConectaPkuDashboard;
